import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  Plus, Save, User, Power, EyeOff, ShieldCheck, Users, ShieldAlert, CheckCircle2, AlertTriangle, CalendarClock, Pencil,
} from 'lucide-react';
import supabase from '../lib/supabase';
import {
  PageHeader, Section, Badge, Button, EmptyState, Modal, Field, Input, Textarea, StatusLine,
} from '../components/ui';
import TimeOffEditor from '../components/TimeOffEditor';

type Coach = {
  id: string;
  display_name: string;
  bio: string | null;
  photo_url: string | null;
  type: 'freelance' | 'staff';
  home_club_id: string | null;
  is_active: boolean;
  verification_status: string;
  years_experience: number | null;
};

type Affiliation = {
  id: string;
  coach_id: string;
  venue_id: string;
  status: 'approved' | 'pending' | 'blocked';
  coaches: { id: string; display_name: string; type: string };
  club_venues: { id: string; name: string; is_primary: boolean } | null;
};

export default function ClubCoaches({ clubId }: { clubId: string }) {
  const [staff, setStaff] = useState<Coach[]>([]);
  const [affiliations, setAffiliations] = useState<Affiliation[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [timeOffFor, setTimeOffFor] = useState<Coach | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [s, a] = await Promise.all([
      supabase
        .from('coaches')
        .select('id, display_name, bio, photo_url, type, home_club_id, is_active, verification_status, years_experience')
        .eq('home_club_id', clubId)
        .eq('type', 'staff')
        .order('display_name'),
      supabase
        .from('coach_affiliations')
        .select('id, coach_id, venue_id, status, coaches(id, display_name, type), club_venues(id, name, is_primary)')
        .eq('club_id', clubId),
    ]);
    setStaff((s.data as Coach[]) ?? []);
    setAffiliations((a.data as unknown as Affiliation[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [clubId]);

  const toggleActive = async (c: Coach) => {
    await supabase.from('coaches').update({ is_active: !c.is_active }).eq('id', c.id);
    load();
  };

  const setAffiliationStatus = async (a: Affiliation, status: 'approved' | 'blocked' | 'pending') => {
    await supabase.from('coach_affiliations').update({ status }).eq('id', a.id);
    load();
  };

  const approveAllPendingForCoach = async (coachId: string) => {
    const ids = affiliations
      .filter((a) => a.coach_id === coachId && a.status === 'pending')
      .map((a) => a.id);
    if (ids.length === 0) return;
    await supabase.from('coach_affiliations')
      .update({ status: 'approved' })
      .in('id', ids);
    load();
  };

  // Group freelance affiliations by coach so we render one block per
  // coach with each of their venue rows underneath. Single-venue clubs
  // collapse to one row each, same shape as before.
  const freelanceGroups = (() => {
    const byCoach = new Map<string, Affiliation[]>();
    for (const a of affiliations) {
      if (a.coaches?.type !== 'freelance') continue;
      const list = byCoach.get(a.coach_id) ?? [];
      list.push(a);
      byCoach.set(a.coach_id, list);
    }
    return Array.from(byCoach.entries()).map(([coachId, rows]) => ({
      coachId,
      coachName: rows[0].coaches?.display_name ?? '—',
      rows: rows.sort((a, b) => {
        const an = a.club_venues?.name ?? '';
        const bn = b.club_venues?.name ?? '';
        return (a.club_venues?.is_primary ? 0 : 1) - (b.club_venues?.is_primary ? 0 : 1)
          || an.localeCompare(bn);
      }),
      pendingCount: rows.filter((r) => r.status === 'pending').length,
    }));
  })();

  return (
    <div className="p-8 max-w-5xl">
      <PageHeader
        eyebrow="Club"
        title="Coaches"
        subtitle="Your staff coaches plus any freelance coaches who teach here."
        actions={<Button icon={Plus} onClick={() => setShowAdd(true)}>Add staff coach</Button>}
      />

      <Section
        title={<span className="flex items-center gap-2"><Users className="w-4 h-4 text-[#2d5016]" /> Staff coaches</span>}
        subtitle="Coaches you employ. They're auto-verified under your club."
        className="overflow-hidden p-0"
      >
        {loading ? (
          <div className="p-8 text-sm text-gray-500 text-center">Loading…</div>
        ) : staff.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No staff coaches yet"
            description="Add your first staff coach so they can list lessons under your club."
            action={<Button icon={Plus} onClick={() => setShowAdd(true)}>Add staff coach</Button>}
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Coach</th>
                <th className="text-left px-5 py-3 font-semibold">Verification</th>
                <th className="text-left px-5 py-3 font-semibold">Experience</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {staff.map((c) => (
                <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                        {c.photo_url
                          ? <img src={c.photo_url} className="w-full h-full object-cover" />
                          : <User className="w-4 h-4 text-gray-400" />}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate">{c.display_name}</div>
                        {c.bio && <div className="text-xs text-gray-500 max-w-xs truncate">{c.bio}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3"><VerificationBadge status={c.verification_status} /></td>
                  <td className="px-5 py-3 text-gray-700">{c.years_experience ? `${c.years_experience} yr` : '—'}</td>
                  <td className="px-5 py-3">
                    <button onClick={() => toggleActive(c)} className="cursor-pointer">
                      {c.is_active
                        ? <Badge tone="green" icon={Power}>Active</Badge>
                        : <Badge tone="gray" icon={EyeOff}>Hidden</Badge>}
                    </button>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex gap-2 justify-end">
                      <Link
                        to={`/club/coaches/${c.id}/profile`}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-white border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Pencil className="w-3.5 h-3.5" /> Edit profile
                      </Link>
                      <Button size="sm" variant="secondary" icon={CalendarClock}
                        onClick={() => setTimeOffFor(c)}>
                        Time off
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section
        title={<span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#2d5016]" /> Freelance coaches teaching here</span>}
        subtitle="Verified independent coaches who offer lessons at your courts, listed per venue so you can approve a coach at one location and decline at another."
        className="overflow-hidden p-0"
      >
        {freelanceGroups.length === 0 ? (
          <EmptyState
            icon={ShieldCheck}
            title="No freelance coaches yet"
            description="When freelance coaches request to teach here, they'll show up in this list."
          />
        ) : (
          <div className="divide-y divide-gray-200">
            {freelanceGroups.map((g) => (
              <div key={g.coachId} className="px-5 py-4">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="text-sm font-medium text-gray-900">{g.coachName}</div>
                  {g.pendingCount > 1 && (
                    <Button size="sm" variant="primary"
                      onClick={() => approveAllPendingForCoach(g.coachId)}>
                      Approve at all {g.pendingCount} venues
                    </Button>
                  )}
                </div>
                <div className="space-y-1.5">
                  {g.rows.map((a) => (
                    <div key={a.id} className="flex items-center justify-between gap-3 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-gray-700 truncate">
                          {a.club_venues?.name ?? <span className="text-gray-400">unknown venue</span>}
                        </span>
                        {a.club_venues?.is_primary && (
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#c6f806]/20 text-[#1a3010]">
                            Primary
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {a.status === 'approved' && <Badge tone="green" icon={CheckCircle2}>Approved</Badge>}
                        {a.status === 'pending'  && <Badge tone="amber">Pending</Badge>}
                        {a.status === 'blocked'  && <Badge tone="red" icon={ShieldAlert}>Blocked</Badge>}
                        {a.status === 'pending' && (
                          <Button size="sm" variant="primary" onClick={() => setAffiliationStatus(a, 'approved')}>
                            Approve
                          </Button>
                        )}
                        {a.status === 'blocked' ? (
                          <Button size="sm" variant="secondary" onClick={() => setAffiliationStatus(a, 'approved')}>
                            Unblock
                          </Button>
                        ) : (
                          <Button size="sm" variant="danger" onClick={() => setAffiliationStatus(a, 'blocked')}>
                            Block
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {showAdd && (
        <StaffCoachForm
          clubId={clubId}
          onClose={() => setShowAdd(false)}
          onSaved={async () => { setShowAdd(false); await load(); }}
        />
      )}

      {timeOffFor && (
        <Modal
          title={`Time off · ${timeOffFor.display_name}`}
          subtitle="Block this coach's calendar for annual leave, days off, or partial time off. The coach sees the same list on their own dashboard."
          onClose={() => setTimeOffFor(null)}
          wide
        >
          <TimeOffEditor coachId={timeOffFor.id} embedded heading="Upcoming time off" />
        </Modal>
      )}
    </div>
  );
}

function VerificationBadge({ status }: { status: string }) {
  if (status === 'verified') return <Badge tone="green" icon={CheckCircle2}>Verified</Badge>;
  if (status === 'rejected') return <Badge tone="red" icon={AlertTriangle}>Rejected</Badge>;
  return <Badge tone="amber">{status.replace('_', ' ')}</Badge>;
}

function StaffCoachForm({
  clubId, onClose, onSaved,
}: { clubId: string; onClose: () => void; onSaved: () => void }) {
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [years, setYears] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const { error } = await supabase.from('coaches').insert([{
      type: 'staff',
      home_club_id: clubId,
      display_name: displayName,
      bio: bio || null,
      years_experience: years ? Number(years) : null,
      verification_status: 'verified',
      verified_at: new Date().toISOString(),
      is_active: true,
    }]);
    setBusy(false);
    if (error) setErr(error.message);
    else onSaved();
  };

  return (
    <Modal
      title="Add staff coach"
      subtitle="Staff coaches are auto-verified by your club. You can add rates from the coach's profile later."
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Display name">
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} required placeholder="e.g. John Smith" />
        </Field>
        <Field label="Short bio">
          <Textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3} placeholder="A sentence or two about their background." />
        </Field>
        <Field label="Years of experience">
          <Input type="number" min="0" value={years} onChange={(e) => setYears(e.target.value)} placeholder="5" />
        </Field>

        {err && <StatusLine kind="error">{err}</StatusLine>}

        <div className="flex gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
          <Button type="submit" loading={busy} icon={Save} className="flex-1">Add coach</Button>
        </div>
      </form>
    </Modal>
  );
}
